export interface PWAInstallationState {
  canInstall: boolean;
  isInstalled: boolean;
  installPrompt: any | null;
}

export class PWAInstallationManager {
  private installPrompt: any = null;
  private listeners: Array<(state: PWAInstallationState) => void> = [];
  private debugMode: boolean = true; // Enable debugging

  constructor() {
    this.debugPWAReadiness();
    this.setupEventListeners();
  }

  private debugPWAReadiness() {
    if (!this.debugMode) return;

    console.log('=== PWA Installation Debug ===');
    console.log('Browser:', navigator.userAgent);
    console.log('Is HTTPS or localhost:', this.isSecureContext());
    console.log('Service Worker supported:', 'serviceWorker' in navigator);
    console.log('Manifest link present:', !!document.querySelector('link[rel="manifest"]'));
    console.log('Current display mode:', this.getCurrentDisplayMode());
    console.log('Is already installed:', this.isAppInstalled());
    
    // Check if app is already installed via different methods
    console.log('Standalone mode:', window.matchMedia('(display-mode: standalone)').matches);
    console.log('iOS standalone:', (window.navigator as any)?.standalone === true);
    console.log('Android app referrer:', document.referrer.includes('android-app://'));

    // Monitor when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        console.log('DOM loaded, checking for manifest...');
        this.checkManifest();
      });
    } else {
      this.checkManifest();
    }
  }

  private isSecureContext(): boolean {
    return location.protocol === 'https:' || 
           location.hostname === 'localhost' || 
           location.hostname === '127.0.0.1';
  }

  private getCurrentDisplayMode(): string {
    const displayModes = ['standalone', 'minimal-ui', 'fullscreen', 'browser'];
    for (const mode of displayModes) {
      if (window.matchMedia(`(display-mode: ${mode})`).matches) {
        return mode;
      }
    }
    return 'unknown';
  }

  private checkManifest() {
    const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    if (manifestLink) {
      console.log('Manifest URL:', manifestLink.href);
      // Try to fetch and validate manifest
      fetch(manifestLink.href)
        .then(response => response.json())
        .then(manifest => {
          console.log('Manifest content:', manifest);
          console.log('Manifest validation:');
          console.log('- Has name or short_name:', !!(manifest.name || manifest.short_name));
          console.log('- Has icons:', !!(manifest.icons && manifest.icons.length > 0));
          console.log('- Has start_url:', !!manifest.start_url);
          console.log('- Has display mode:', !!manifest.display);
        })
        .catch(err => console.error('Failed to load manifest:', err));
    } else {
      console.error('No manifest link found in document head');
    }
  }

  private setupEventListeners() {
    console.log('Setting up PWA event listeners...');
    
    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('🎉 beforeinstallprompt event fired!', e);
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      this.installPrompt = e;
      this.notifyListeners();
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      console.log('🎉 PWA was installed');
      this.installPrompt = null;
      this.notifyListeners();
    });

    // Additional debugging - check for service worker registration
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        console.log('Service Worker ready:', registration);
      }).catch((error) => {
        console.error('Service Worker registration failed:', error);
      });
    }

    // Monitor for changes in installation eligibility
    setTimeout(() => {
      if (!this.installPrompt && this.debugMode) {
        console.log('⚠️ No beforeinstallprompt event received after 3 seconds');
        console.log('This could be because:');
        console.log('1. App is already installed');
        console.log('2. PWA criteria not met (check manifest, HTTPS, service worker)');
        console.log('3. Browser doesn\'t support installation (Safari, Firefox)');
        console.log('4. User has previously dismissed the prompt');
        console.log('5. App doesn\'t meet engagement heuristics');
      }
    }, 3000);
  }

  private notifyListeners() {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }

  public getState(): PWAInstallationState {
    const state = {
      canInstall: this.installPrompt !== null,
      isInstalled: this.isAppInstalled(),
      installPrompt: this.installPrompt
    };
    
    if (this.debugMode) {
      console.log('Current PWA state:', state);
    }
    
    return state;
  }

  private isAppInstalled(): boolean {
    // Check if running in standalone mode (already installed)
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any)?.standalone === true ||
           document.referrer.includes('android-app://');
  }

  public async promptInstall(): Promise<{ outcome: 'accepted' | 'dismissed' | 'error', error?: string }> {
    if (!this.installPrompt) {
      return { outcome: 'error', error: 'No install prompt available' };
    }

    try {
      const result = await this.installPrompt.prompt();
      const outcome = result.outcome;
      
      // Clear the prompt as it can only be used once
      this.installPrompt = null;
      this.notifyListeners();

      return { outcome };
    } catch (error) {
      return { outcome: 'error', error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  public addListener(listener: (state: PWAInstallationState) => void) {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public canShowInstallButton(): boolean {
    const state = this.getState();
    return state.canInstall && !state.isInstalled;
  }

  public canShowManualInstallInstructions(): boolean {
    // Show manual instructions if:
    // 1. Not already installed
    // 2. No automatic install prompt available
    // 3. Running on a supported browser
    return !this.isAppInstalled() && 
           !this.installPrompt && 
           this.isBrowserSupported();
  }

  private isBrowserSupported(): boolean {
    // Check if running on a browser that supports PWA installation
    // (even if beforeinstallprompt isn't available)
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('chrome') || 
           userAgent.includes('edge') || 
           userAgent.includes('samsung') ||
           userAgent.includes('safari'); // Safari supports Add to Home Screen
  }

  public getManualInstallInstructions(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return 'Tap the Share button in Safari, then select "Add to Home Screen"';
    }
    
    if (userAgent.includes('chrome') || userAgent.includes('edge')) {
      return 'Look for the install icon in the address bar, or use the browser menu to "Install App"';
    }
    
    if (userAgent.includes('firefox')) {
      return 'Firefox doesn\'t support automatic PWA installation. You can bookmark this page for quick access.';
    }
    
    return 'Look for an install or "Add to Home Screen" option in your browser menu';
  }

  public setDebugMode(enabled: boolean) {
    this.debugMode = enabled;
  }
}

// Create a singleton instance
export const pwaInstallationManager = new PWAInstallationManager();